import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Checkbox, Input, Upload, Button, Select, Typography } from 'antd';
import { UploadOutlined } from '@ant-design/icons';

import {
  StyledModal,
  Form,
  WrapperSelect,
  WithLoading,
} from 'common/components';
import APLModalFooter from './APLModalFooter';

import { APL_VISIBILITY } from 'static/Constants';

import { useCreateEditApl } from 'pages/home/ribbon/components/sections/product-apl/hooks';
import { useGetAPLDetail } from '../hooks';
import { useGlobalModal } from 'hooks';

import gridViewSelectors from 'common/components/grid-view/controllers/selectors';

import { MASTER_APL_NAME } from 'pages/home/ribbon/components/sections/product-apl/constants';

const CreateEditAplModal = ({ aplId }) => {
  const selectedAPLs = useSelector(
    gridViewSelectors.makeSelectDetailCurrentITemsSelection()
  );

  const { checkModalVisible, MODAL_NAMES } = useGlobalModal();

  const isEdit = checkModalVisible(MODAL_NAMES.EDIT_APL);
  const isCreatingMasterApl = checkModalVisible(MODAL_NAMES.CREATE_MASTER_APL);

  const getIdAPL = () => {
    if (!isEdit) return null;

    if (aplId) return aplId;

    const { id } = selectedAPLs?.[0] || {};

    return id;
  };

  const id = getIdAPL();

  const LAYOUT_MASTER_APL_FORM = {
    labelCol: { span: 8 },
    wrapperCol: { span: 16 },
    initialValues: isCreatingMasterApl
      ? {
          aplName: MASTER_APL_NAME,
          visibility: 'Company',
          aplDescription: 'Entire Product Listing',
        }
      : null,
  };

  const {
    isModalOpen,
    isSubmitLoading,
    handleSubmitForm,
    uploadProps,
    closeAllModal,
    formInstance,
  } = useCreateEditApl({ id, isEdit });

  const enabled = isEdit && id && isModalOpen;

  const { data, isLoading } = useGetAPLDetail({ id, enabled });

  const isMasterApl = data?.aplName === MASTER_APL_NAME || isCreatingMasterApl;
  const initLoadingEditMode = isLoading && enabled;

  useEffect(() => {
    if (isEdit && data)
      formInstance.setFieldsValue({
        aplName: data?.aplName,
        aplLongDescription: data.aplLongDescription,
        aplDescription: data.aplDescription,
        enableScheduleMatching: data.enableScheduleMatching,
        visibility: data.visibility,
      });
  }, [isEdit, formInstance, data]);

  return (
    <>
      <StyledModal
        title={`${isEdit ? 'Edit APL' : 'Create APL'}`}
        visible={isModalOpen}
        onCancel={() => closeAllModal()}
        cancelText={'Cancel'}
        maskClosable={false}
        bodyStyle={{ height: '43vh' }}
        width={'35%'}
        accept={'.xlsx, .xls, .csv'}
        confirmLoading={isSubmitLoading}
        destroyOnClose={true}
        footer={
          <APLModalFooter
            isEdit={isEdit}
            isSubmitLoading={isSubmitLoading}
            handleSubmitForm={handleSubmitForm}
          />
        }
      >
        <WithLoading loading={initLoadingEditMode}>
          <Form {...LAYOUT_MASTER_APL_FORM} form={formInstance}>
            <Form.Item
              name='aplName'
              label='Name'
              rules={[{ required: true, message: 'Please input APL name.' }]}
            >
              <Input disabled={isMasterApl} />
            </Form.Item>
            <Form.Item name='aplDescription' label='Description'>
              <Input disabled={isMasterApl} />
            </Form.Item>
            <Form.Item name='aplLongDescription' label='More Description'>
              <Input.TextArea />
            </Form.Item>
            {!isEdit && (
              <Form.Item name='notes' label='Note'>
                <Input.TextArea />
              </Form.Item>
            )}

            <Form.Item name='visibility' label='Visibility'>
              <WrapperSelect disabled={isMasterApl}>
                {APL_VISIBILITY.map((item) => (
                  <Select.Option key={item} value={item}>
                    {item}
                  </Select.Option>
                ))}
              </WrapperSelect>
            </Form.Item>
            <Form.Item
              name='enableScheduleMatching'
              label='Schedule Matching'
              valuePropName='checked'
            >
              <Checkbox />
            </Form.Item>
            {!isEdit && (
              <Form.Item
                name='templateFile'
                label='APL Items'
                className='form-item__upload-file'
              >
                <Upload {...uploadProps}>
                  <Button icon={<UploadOutlined />}>Select File</Button>
                  <Typography.Text className='form-item__hint-upload'>
                    (*) Only support types: .xlsx, .xls, .csv
                  </Typography.Text>
                </Upload>
              </Form.Item>
            )}
          </Form>
        </WithLoading>
      </StyledModal>
    </>
  );
};

export default CreateEditAplModal;
